import axios, { AxiosInstance } from "axios";
import { APP_PATH } from "../AppConstants";
import { successResponseInterceptor, errorResponseInterceptor } from "../infrastructure/global";
import { IVehicle, IContactInfo, ICoverageConfig, IDriver, IDiscountOption } from "../classes/Domain";

export default class QuoteApiClient {
    instance: AxiosInstance;

    /**
     * Shared root API URL for all states (Q18). stateCode is ignored for routing —
     * state comes from zip/USPS on the quote, not from /tx|/ok path prefix.
     */
    constructor(_stateCode: string | null = null) {
        this.instance = axios.create({
            baseURL: APP_PATH,
            method: 'post'
        });

        this.instance.interceptors.response.use(
            successResponseInterceptor,
            errorResponseInterceptor
        );
    }

    validateQuotePostData() {
        return this.instance.request({
            url: '/quote/validate-quote-post',
            data: {}
        });
    }

    postCreateQuote() {
        return this.instance.request({
            url: '/quote/create',
            data: {}
        });
    }

    createQuote(program: string, zipCode: string, city: string, stateCode: string, vehicle?: IVehicle | null) {
        return this.instance.request({
            url: '/quote/create',
            data: {
                zipCode,
                city,
                stateCode,
                program,
                vehicle
            }
        });
    }

    updateQuickQuote(quoteUid: string, vehicles: Array<IVehicle>, drivers: Array<IDriver>, coverageConfig: ICoverageConfig, discounts: IDiscountOption, contactInfo: IContactInfo, isNonOwners: boolean, quickStep: string) {
        return this.instance.request({
            url: '/quote/update-quick-quote',
            data: {
                quoteUid,
                vehicles,
                drivers,
                coverageConfig,
                discounts,
                contactInfo,
                isNonOwners,
                quickStep
            }
        });
    }

    updateContactInfo(quoteUid: string, contactInfo: IContactInfo, quoteStep: string) {
        return this.instance.request({
            url: '/quote/update-contact',
            data: {
                quoteUid,
                contactInfo,
                quoteStep
            }
        });
    }

    getOccupations(query: string) {
        return this.instance.request({
            url: '/quote/get-occupations?query=' + query,
            method: 'get'
        });
    }

    getQuote(quoteUid: string) {
        return this.instance.request({
            url: '/quote/get-quote?quoteUid=' + quoteUid,
            method: 'get'
        });
    }

    getContactInfo(quoteUid: string) {
        return this.instance.request({
            url: '/quote/get-contact?quoteUid=' + quoteUid,
            method: 'get'
        });
    }

    updateQuoteAgency(quoteUid: string, producerCode: string, storeNumber: string) {
        return this.instance.request({
            url: '/quote/update-quote-agency',
            data: {
                quoteUid,
                producerCode,
                storeNumber
            }
        });
    }

    getCoverageConfigs(quoteUid: string) {
        return this.instance.request({
            url: '/quote/coverages/configs',
            data: {
                uid: quoteUid
            }
        });
    }

    getRates(quoteUid: string, coverageConfig: ICoverageConfig) {
        return this.instance.request({
            url: '/quote/get-rate',
            data: {
                uid: quoteUid,
                coverageConfig
            }
        });
    }

    updateDriver(quoteUid: string, driver: IDriver, quoteStep: string, stateCode: string | null = null) {
        return this.instance.request({
            url: '/quote/update-driver',
            data: {
                quoteUid,
                driver,
                quoteStep,
                stateCode
            }
        });
    }

    updateInsurance(quoteUid: string, currentInsurance: any, quoteStep: string) {
        return this.instance.request({
            url: '/quote/update-insurance',
            data: {
                quoteUid,
                currentInsurance,
                quoteStep
            }
        });
    }

    getInsuranceCompanies() {
        return this.instance.request({
            url: '/quote/get-insurance-companies'
        });
    }

    getVehicleMakes(year: number, quoteId: number, quoteUid: string) {
        return this.instance.request({
            url: '/quote/vehicle/get-makes',
            data: {
                year: year,
                quoteId: quoteId,
                quoteUid: quoteUid
            }
        });
    }

    getVehicleModels(year: number, make: string, quoteUid: string) {
        return this.instance.request({
            url: '/quote/vehicle/get-models',
            data: {
                year: year,
                make: make,
                quoteUid: quoteUid
            }
        });
    }

    getVinRecords(year: number, make: string, model: string, quoteUid: string, vin: string | null) {
        return this.instance.request({
            url: '/quote/vehicle/get-vin-records',
            data: {
                year: year,
                make: make,
                model: model,
                quoteUid: quoteUid,
                vin: vin
            }
        });
    }

    updateVehicle(quoteUid: string, vehicle: IVehicle, quoteStep: string) {
        return this.instance.request({
            url: '/quote/update-vehicle',
            data: {
                quoteUid,
                vehicle,
                quoteStep
            }
        });
    }

    getSubRates() {
        return Promise.reject();
    }

    getOfflineData() {
        return this.instance.request({
            url: '/quote/get-offline-data',
            method: 'get'
        });
    }
}